import { useCallback, useEffect, useState } from 'react';
import { Box, TableCell, TableRow, Tooltip } from '@mui/material';
import { Formik, Form } from 'formik';
import dayjs from 'dayjs';
// Our Components
import TransactionsCategoryInput from './TransactionsCategoryInput';
// Services
import { Cards } from 'lib/api';
// Utils and Helpers
import { numberFormat } from 'helpers/i18n';
// Assets
import { ReactComponent as MemoIcon } from 'assets/MemoIcon.svg';
import { ReactComponent as PaystandNetworkLogo } from 'assets/transactionsSection/paystand_network_logo.svg';
import { ReactComponent as ReceiptAndMemoIcon } from 'assets/ReceiptAndMemoIcon.svg';
import { ReactComponent as ReceiptIcon } from 'assets/ReceiptIcon.svg';
import { ReactComponent as UploadIcon } from 'assets/UploadIcon.svg';

const TransactionTableRow = ({
    transaction,
    setSelectedCardTransactionID,
    isTransactionDetailsOpen,
    setIsTransactionDetailsOpen,
    statusFormatter,
    transactionsCategories,
    transactionsCategoriesRaw
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [parentCardTransaction, setParentCardTransaction] = useState(null);

    const fetchParentCardTransaction = useCallback(async () => {
        try {
            const parentCardTransactionData = await Cards.getCardTransaction(transaction?.id_parent_card_transaction);
            setParentCardTransaction(parentCardTransactionData);
        } catch (error) {
            console.error(error);
        }
    }, [transaction.id_parent_card_transaction]);
    useEffect(() => {
        if (transaction.id_parent_card_transaction) {
            fetchParentCardTransaction();
        }
    }, [fetchParentCardTransaction, transaction.id_parent_card_transaction]);

    const onMouseEnterEvent = () => {
        setIsHovered(true);
    };

    const onMouseLeaveEvent = () => {
        setIsHovered(false);
    };

    const onRowClickEvent = () => {
        setSelectedCardTransactionID(transaction.id);
        setIsTransactionDetailsOpen(isTransactionDetailsOpen => !isTransactionDetailsOpen);
    };

    return (
        <TableRow
            role="button"
            onClick={onRowClickEvent}
            onMouseEnter={onMouseEnterEvent}
            onMouseLeave={onMouseLeaveEvent}
            className={isTransactionDetailsOpen ? 'transaction-row-active' : 'transaction-row'}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <TableCell component="th" scope="row">
                <Box className="fw-bold" sx={{ marginBottom: '5px', marginRight: '-20px', color: '#1BABE2' }}>
                    {'...' + transaction.id.substring(transaction.id.length - 6)}
                </Box>
            </TableCell>
            <TableCell component="th" scope="row">
                <Box sx={{ marginBottom: '5px', color: '#182C55' }}>{transaction.name}</Box>
                <Box className="gray-text">*{transaction.last4}</Box>
            </TableCell>
            <TableCell className="card-table-row-cell">
                {!!transaction?.isPaystandNet && (
                    <Box className="fw-bold" display="inline-block" alignItems="center" sx={{ fontSize: '10px' }}>
                        <PaystandNetworkLogo style={{ fontSize: '10px', marginRight: '6px', marginBottom: '-2px' }} />
                    </Box>
                )}
                <Box sx={{ marginBottom: '3px', display: 'inline-block' }}>{transaction.merchant}</Box>
            </TableCell>
            <TableCell className="card-table-row-cell" onClick={event => event.stopPropagation()}>
                <Box sx={{ marginBottom: '5px' }}>
                    <Formik initialValues={{ label: '' }}>
                        <Form id="transaction-category-form">
                            <TransactionsCategoryInput
                                variant="outlined"
                                name="label"
                                size="small"
                                transaction={transaction.id_parent_card_transaction ? parentCardTransaction : transaction}
                                transactionsCategories={transactionsCategories}
                                transactionsCategoriesRaw={transactionsCategoriesRaw}
                                isHovered={isHovered}
                                parentComponentName="TransitionRow"
                                show
                            />
                        </Form>
                    </Formik>
                </Box>
            </TableCell>
            <TableCell className="card-table-row-cell">{transaction.department}</TableCell>
            <TableCell className="card-table-row-cell">{dayjs(transaction.solid_created_at).format('MMM DD, YYYY')}</TableCell>
            <TableCell>{statusFormatter(transaction)}</TableCell>
            <TableCell flex className="card-table-row-cell">
                <Box display="flex" flexDirection="row" justifyContent="flex-end">
                    <Box className="fw-bold">{numberFormat(transaction.amount, 2, 2)}</Box>

                    {(transaction.memo || parentCardTransaction?.memo) &&
                    (transaction.hasReceipts || parentCardTransaction?.receipts.length > 0) ? (
                        <Box marginLeft="20px" className="fw-bold">
                            <Tooltip title="Memo & receipt available">
                                <ReceiptAndMemoIcon />
                            </Tooltip>
                        </Box>
                    ) : (
                        <>
                            {transaction.memo || parentCardTransaction?.memo ? (
                                <Box marginLeft="20px" className="fw-bold">
                                    <Tooltip title="Memo available">
                                        <MemoIcon />
                                    </Tooltip>
                                </Box>
                            ) : null}

                            {transaction.hasReceipts || parentCardTransaction?.receipts.length > 0 ? (
                                <Box marginLeft="20px" className="fw-bold">
                                    <Tooltip title="Receipt available">
                                        <ReceiptIcon />
                                    </Tooltip>
                                </Box>
                            ) : null}

                            {!(transaction.memo || parentCardTransaction?.memo) &&
                                !(transaction.hasReceipts || parentCardTransaction?.receipts.length > 0) && (
                                    <Box marginLeft="20px" className="fw-bold">
                                        <UploadIcon />
                                    </Box>
                                )}
                        </>
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );
};

export default TransactionTableRow;
